/* eslint-disable complexity */
import React, { createContext, ReactNode, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ExtendedForecastListItem } from '@futrli/views/processors/forecasts/forecastsList';
import { ForecastListItem } from '@futrli-sdk/predict';
import {
  getConsolidatedForecastSalesUrl,
  getPredictForecastInvoicesUrl,
  getPredictForecastSalesUrl,
  matchConsolidationDetailRoutePath,
  PREDICT_PRODUCT_SLUG,
  useModal
} from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { DeleteForecastDialogContainer } from '../../dialogs/delete-forecast-dialog/delete-forecast-dialog-container';
import { DuplicateForecastSidebar } from '../../sidebars/duplicate-forecast-sidebar/duplicate-forecast-sidebar';
import { ExcelExportSidebar } from '../../sidebars/excel-export-sidebar/excel-export-sidebar';
import { GoogleSheetsExportSidebar } from '../../sidebars/google-sheets-export-sidebar/google-sheets-export-sidebar';
import { LockForecastDialogContainer } from '../../dialogs/lock-forecast-dialog/lock-forecast-dialog-container';
import { PublishForecastDialogContainer } from '../../dialogs/publish-forecast-dialog/publish-forecast-dialog-container';
import { UpdateForecastSidebar } from '../../sidebars/update-forecast-sidebar/update-forecast-sidebar';

interface ForecastActionsContextValue {
  handleDeleteClick: (forecast: ExtendedForecastListItem) => void;
  handleDuplicateClick: (forecast: ExtendedForecastListItem) => void;
  handleEditClick: (forecast: ExtendedForecastListItem) => void;
  handleExcelExportClick: (forecast: ExtendedForecastListItem) => void;
  handleExportClick: (forecast: ExtendedForecastListItem) => void;
  handleLockClick: (forecast: ExtendedForecastListItem) => void;
  handleNameClick: (forecast: ForecastListItem, isConsolidation: boolean) => void;
  handlePublishClick: (forecast: ExtendedForecastListItem) => void;
  handleShareClick: (forecast: ExtendedForecastListItem) => void;
}

const ForecastActionsContext = createContext<ForecastActionsContextValue>(
  ({} as unknown) as ForecastActionsContextValue
);

export const ForecastActionsProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const { shareResource } = useSidebarContext();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const deleteDialog = useModal();
  const lockDialog = useModal();
  const publishDialog = useModal();

  const duplicateSidebar = useModal();
  const editSidebar = useModal();
  const excelExportSidebar = useModal();
  const googleSheetsExportSidebar = useModal();

  const [forecast, setForecast] = React.useState<ExtendedForecastListItem>();

  const handleDeleteClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      deleteDialog.open();
    },
    [deleteDialog]
  );

  const handleDuplicateClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      duplicateSidebar.open();
    },
    [duplicateSidebar]
  );

  const handleEditClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      editSidebar.open();
    },
    [editSidebar]
  );

  const handleExportClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      googleSheetsExportSidebar.open();
    },
    [googleSheetsExportSidebar]
  );

  const handleExcelExportClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      excelExportSidebar.open();
    },
    [excelExportSidebar]
  );

  const handleLockClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      lockDialog.open();
    },
    [lockDialog]
  );

  const handleNameClick = useCallback(
    (forecast: ForecastListItem, isConsolidation: boolean) => {
      if (isConsolidation) {
        navigate(getConsolidatedForecastSalesUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG, forecast.id));
      } else {
        if (forecast.kind === 'scenario' && forecast.include_unpaid_invoices) {
          navigate(getPredictForecastInvoicesUrl(workspaceId, organisationId, forecast.id));
        } else {
          navigate(getPredictForecastSalesUrl(workspaceId, organisationId, forecast.id));
        }
      }
    },
    [navigate, workspaceId, organisationId]
  );

  const handlePublishClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      setForecast(forecast);
      publishDialog.open();
    },
    [publishDialog]
  );

  const handleShareClick = useCallback(
    (forecast: ExtendedForecastListItem) => {
      shareResource.open({
        entityId: forecast.id,
        entityType: isConsolidation ? 'consolidated-forecast' : 'forecast',
        resourceName: forecast.name
      });
    },
    [isConsolidation, shareResource]
  );

  const value = {
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handleExcelExportClick,
    handleExportClick,
    handleLockClick,
    handleNameClick,
    handlePublishClick,
    handleShareClick
  };

  return (
    <ForecastActionsContext.Provider value={value}>
      <DeleteForecastDialogContainer
        forecastId={forecast?.id}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      />
      {googleSheetsExportSidebar.isOpen && (
        <GoogleSheetsExportSidebar
          onClose={googleSheetsExportSidebar.close}
          isConsolidation={false}
          forecastId={forecast?.id}
          forecastKind={forecast?.kind}
          forecastName={forecast?.name}
        />
      )}
      {excelExportSidebar.isOpen && <ExcelExportSidebar onClose={excelExportSidebar.close} forecastId={forecast?.id} />}
      <PublishForecastDialogContainer
        forecastId={forecast?.id}
        isOpen={publishDialog.isOpen}
        onClose={publishDialog.close}
      />
      <LockForecastDialogContainer forecastId={forecast?.id} isOpen={lockDialog.isOpen} onClose={lockDialog.close} />

      <DuplicateForecastSidebar
        forecastId={forecast?.id}
        isOpen={duplicateSidebar.isOpen}
        onClose={duplicateSidebar.close}
      />
      <UpdateForecastSidebar forecastId={forecast?.id} isOpen={editSidebar.isOpen} onClose={editSidebar.close} />

      {children}
    </ForecastActionsContext.Provider>
  );
};

export const useForecastActions = () => {
  const context = useContext(ForecastActionsContext);
  if (!context) {
    throw new Error('useForecastActions must be used within a ForecastActionsProvider');
  }
  return context;
};
